/**
 * @fileoverview Atom: a labelled form control.
 * @module components/atoms/Input
 */

import { useId, type InputHTMLAttributes } from "react"

import "./Input.css"

/** Props accepted by {@link Input}. */
export interface InputProps extends Omit<
	InputHTMLAttributes<HTMLInputElement>,
	"className" | "id"
> {
	/** Visible label. Always present: a placeholder is not a label. */
	readonly label: string
	/** Supporting text shown under the control. */
	readonly hint?: string
	/** Validation message. Its presence marks the field invalid. */
	readonly error?: string
}

/**
 * Labelled text or number input.
 *
 * @param props - Label, optional hint and error, plus native input props.
 * @returns The rendered field, label and messages wired for accessibility.
 *
 * @example
 * <Input label="Prix" type="number" min={0} required />
 */
export function Input({ label, hint, error, ...rest }: InputProps) {
	const id = useId()
	const hintId = `${id}-hint`
	const errorId = `${id}-error`

	const describedBy =
		[
			hint === undefined ? null : hintId,
			error === undefined ? null : errorId,
		]
			.filter((value): value is string => value !== null)
			.join(" ") || undefined

	return (
		<div className="field">
			<label className="field__label" htmlFor={id}>
				{label}
			</label>
			<input
				id={id}
				className="field__control"
				aria-invalid={error === undefined ? undefined : true}
				{...(describedBy === undefined
					? {}
					: { "aria-describedby": describedBy })}
				{...rest}
			/>
			{hint !== undefined && (
				<span className="field__hint" id={hintId}>
					{hint}
				</span>
			)}
			{error !== undefined && (
				<span className="field__error" id={errorId} role="alert">
					{error}
				</span>
			)}
		</div>
	)
}
